import { useBottomSheetGestures } from '../../hooks/useBottomSheetGestures.js'

import { BottomSheet } from '../BottomSheet/index.js'
import { Button } from '../Button/index.js'
import { ChevronLeft } from '../icons/ChevronLeft.jsx'
import { ChevronRight } from '../icons/ChevronRight.jsx'

import styles from './MobileProductCarouselSheet.module.scss'

/**
 * MobileProductCarouselSheet
 * 
 * Presentational carousel component for mobile preview sheets (Tiers, Hero).
 * Parent manages all state (carousel position, swipe tracking).
 * This component only handles gesture capture and layout.
 * 
 * @param {Object} props
 * @param {Object} props.active - Currently displayed product
 * @param {boolean} props.isOpen - Whether sheet is open
 * @param {boolean} props.isMinimized - Whether sheet is minimized
 * @param {Function} props.onClose - Close callback
 * @param {Function} props.onMinimize - Minimize callback
 * @param {Function} props.onExpand - Expand callback
 * @param {Function} props.onNavigatePrev - Navigate to previous product
 * @param {Function} props.onNavigateNext - Navigate to next product
 * @param {Function} props.renderProductContent - (product, mode) => ReactNode
 * @param {string} props.headerTitle - Product identifier for header
 * @param {ReactNode} props.headerBadges - Status/offer badges to display in header
 * @param {string} props.bottomInset - CSS var or value for safe-area inset
 * @param {string} props.className - Additional classes for BottomSheet
 * @param {string} props.mode - Current preview mode (resolved display mode)
 * @param {number} props.swipeOffset - Current carousel translateX offset (px)
 * @param {string} props.swipeActionSide - Visual hint: 'left', 'right', or null
 * @param {Function} props.onSwipeMove - Callback for swipe movement tracking
 */
export function MobileProductCarouselSheet({
  active = null,
  isOpen = false,
  isMinimized = false,
  onClose,
  closeLabel = 'Close',
  minimizeLabel = 'Minimize',
  expandLabel = 'Expand',
  onMinimize,
  onExpand,
  onNavigatePrev,
  onNavigateNext,
  renderProductContent,
  headerTitle,
  headerBadges,
  bottomInset,
  className,
  mode,
  swipeOffset = 0,
  swipeActionSide = null,
  onSwipeMove,
}) {
  // Sheet gesture handlers: expand/minimize/close and swipe navigation
  const {
    onBodyTouchStart,
    onBodyTouchMove,
    onBodyTouchEnd,
  } = useBottomSheetGestures({
    isMinimized,
    onExpand,
    onMinimize,
    onClose,
    onSwipePrev: onNavigatePrev,
    onSwipeNext: onNavigateNext,
    onBodySwipeMove: onSwipeMove,
  })

  if (!active) return null

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      closeLabel={closeLabel}
      minimizeLabel={minimizeLabel}
      expandLabel={expandLabel}
      showMinimizeButton
      blocking={false}
      isMinimized={isMinimized}
      onExpand={onExpand}
      onMinimize={onMinimize}
      bottomInset={bottomInset}
      className={className}
    >
      {/* Header must be a direct BottomSheet child so BottomSheet can inject drag + system actions */}
      <BottomSheet.Header>
        <div className={styles.headerContent}>
          <span className={styles.headerTitle}>{headerTitle}</span>
          {headerBadges && <div className={styles.headerBadges}>{headerBadges}</div>}
        </div>
      </BottomSheet.Header>

      {/* Body: carousel with nav buttons and swipe gestures */}
      <div
        className={[
          styles.body,
          isMinimized ? styles.bodyMinimized : '',
        ].filter(Boolean).join(' ')}
        onTouchStart={onBodyTouchStart}
        onTouchMove={onBodyTouchMove}
        onTouchEnd={onBodyTouchEnd}
      >
        <div
          className={[
            styles.carousel,
            swipeActionSide ? styles.carouselHint : '',
            swipeActionSide === 'left' ? styles.carouselPrev : '',
            swipeActionSide === 'right' ? styles.carouselNext : '',
          ].filter(Boolean).join(' ')}
          style={{ transform: `translateX(${swipeOffset}px)` }}
        >
          {/* Previous button */}
          <Button
            type="button"
            kind="icon"
            size="large"
            variant="ghost"
            onClick={onNavigatePrev}
            aria-label="Previous product"
            className={styles.arrowButton}
          >
            <ChevronLeft />
          </Button>

          {/* Product content (via render function) */}
          {renderProductContent()}

          {/* Next button */}
          <Button
            type="button"
            kind="icon"
            size="large"
            variant="ghost"
            onClick={onNavigateNext}
            aria-label="Next product"
            className={styles.arrowButton}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </BottomSheet>
  )
}
